import {
  anyEquals,
  equals,
  rejectNil,
  rejectUndefined,
} from '@unimpaired/utils'
import { addMinutes, differenceInMinutes, isAfter, max, min } from 'date-fns'
import { flow, pipe } from 'fp-ts/lib/function'
import * as R from 'ramda'
import {
  ColumnateByObj,
  DayScheduleProps,
  ScheduleEvent,
} from './DaySchedule.types'

/**
 * Sub-cols allocated to each column group (venue / category / etc). The
 * total grid scales linearly: `columnsPerColumnGroup * numColumnGroups`,
 * so every group keeps a generous sub-grid for sibling/staggered placement
 * and overflow truncation regardless of how many groups the schedule has.
 *
 * Was 60 historically — every parallel CSS grid (events, headings,
 * separators) declared `repeat(60 * N, …)` tracks, and on a dense week
 * (~21 clusters × 3 grids × 6 groups) the browser had ~22k grid tracks
 * to resolve at first paint. 12 still gives ~8% sub-column precision for
 * staggered/indented events while cutting layout work 5× from the
 * original.
 */
export const columnsPerColumnGroup = 24

/**
 * Total grid columns when no columnation is configured (the single-group
 * fallback). Equal to `getTotalDayScheduleColumns(1)`. Also used by
 * `DayScheduleEvent` as a safe `colWidth` fallback when an event hasn't
 * been positioned.
 */
export const totalDayScheduleColumns = columnsPerColumnGroup

/**
 * Total grid columns for a schedule with `numColumnGroups` column groups
 * — `columnsPerColumnGroup` cols per group, floored at one group's worth
 * so a non-columnated schedule still has a usable grid.
 */
export const getTotalDayScheduleColumns = (numColumnGroups = 1): number =>
  columnsPerColumnGroup * Math.max(1, numColumnGroups)

/**
 * Pixel-width floor for an indented event's indent zone (the columns
 * between its parent recursion's left edge and its own colStart). Without
 * this minimum, CSS Grid satisfies the parent event's min-content by
 * piling all the width onto the right side and collapsing the indent cols
 * to ~0, which erases the visual cue that the event is staggered under
 * its parent. Placeholder elements at the indent zone (rendered in each
 * of the three sibling grids) carry this `minWidth` so the tracks
 * actually hold the indent open.
 */
export const indentZoneMinWidthPx = 16

/**
 * Allocate a slice of the column-group grid to a single group (e.g. one
 * venue's worth of cols, given Studio A / B / C). Returns 1-indexed
 * `start` and `span`. The math mirrors what `positionEvents` uses
 * internally so that headings, column separators, and events can all
 * share the same `gridTemplateColumns` and line up visually. The 1-or-2-
 * col gap between adjacent groups is what makes room for the vertical
 * separator line.
 */
export const getColumnGroupAllocation = (
  totalGroups: number,
  groupIdx: number,
  /**
   * Optional per-group weights. When provided, the grid is divided
   * proportionally to these weights so column-groups with more concurrent
   * events get more cols (and therefore more pixel width). When omitted,
   * cols are split equally — the original behavior.
   */
  weights?: number[],
): { start: number; span: number } => {
  const totalCols = getTotalDayScheduleColumns(totalGroups)
  if (!weights || weights.length !== totalGroups) {
    const baseChunk = Math.floor(totalCols / totalGroups)
    const isMiddle = groupIdx > 0 && groupIdx !== totalGroups - 1
    const span = baseChunk - 1 - (isMiddle ? 1 : 0)
    const offset = baseChunk * groupIdx + (groupIdx > 0 ? 1 : 0)
    return { start: offset + 1, span }
  }

  /**
   * Weighted: reserve 2-col gap per boundary, distribute remaining cols by
   * weight, floor at 1.
   */
  const gapCols = 2
  const numGaps = Math.max(0, totalGroups - 1)
  const eventCols = totalCols - numGaps * gapCols
  const totalWeight = weights.reduce(R.add, 0)
  if (totalWeight === 0) {
    return getColumnGroupAllocation(totalGroups, groupIdx)
  }
  const rawSpans = weights.map((w) =>
    Math.max(1, Math.round((eventCols * w) / totalWeight)),
  )
  /**
   * Rounding can leave a remainder — apply it to the last group so the grid
   * sums to exactly `eventCols`.
   */
  const remainder = eventCols - rawSpans.reduce(R.add, 0)
  const spans = R.update(
    rawSpans.length - 1,
    Math.max(1, rawSpans[rawSpans.length - 1] + remainder),
    rawSpans,
  )

  const start =
    1 + spans.slice(0, groupIdx).reduce((acc, span) => acc + span + gapCols, 0)
  return { start, span: spans[groupIdx] }
}

/**
 * Sweep-line: the maximum number of `events` that are concurrent at any
 * single instant. An event ending exactly when another starts is NOT
 * counted as concurrent with it — we sort end-deltas (-1) ahead of
 * start-deltas (+1) at tied timestamps so the running count drops before
 * the next event opens.
 */
const maxConcurrentEvents = <T extends ScheduleEvent>(events: T[]): number =>
  pipe(
    events.flatMap((e) => [
      { t: e.startTime.getTime(), delta: 1 },
      { t: e.endTime.getTime(), delta: -1 },
    ]),
    R.sortWith<{ t: number; delta: number }>([
      R.ascend(R.prop('t')),
      R.ascend(R.prop('delta')),
    ]),
    R.reduce(
      ({ current, max }, { delta }) => {
        const next = current + delta
        return { current: next, max: Math.max(max, next) }
      },
      { current: 0, max: 0 },
    ),
    R.prop('max'),
  )

/**
 * Per-group weights for `getColumnGroupAllocation`: each weight is the
 * max number of concurrent events for that column group. Pass the same
 * `columnHeadings` array your layout uses elsewhere so the weight indices
 * line up with the group indices. Returns `undefined` when columnation
 * isn't configured (the equal-allocation default applies in that case).
 */
export const getColumnGroupWeights = <T extends ScheduleEvent>(
  events: T[],
  columnHeadings: { key?: string }[] | undefined,
  columnateBy: DayScheduleProps<T>['columnateBy'],
): number[] | undefined => {
  if (!columnHeadings || !columnateBy) return undefined
  return columnHeadings.map(({ key }) => {
    const groupEvents = events.filter(
      (e) => getColumnEventIsIn(columnateBy)(e)?.key === key,
    )
    return Math.max(1, maxConcurrentEvents(groupEvents))
  })
}

// ── Grid row math — positions a single event vertically in the 5-min grid ──

/**
 * Calculate the CSS `grid-row` value for an event placed in a 5-minute
 * increment grid. Trims events starting before the day or running past
 * its end, and enforces a minimum display duration so very short events
 * are still clickable.
 *
 * Grid layout invariant: 12 rows per hour (one per 5 min), and the first
 * row is reserved for the column heading — hence `+ 1` below.
 */
export const getGridRow = (args: {
  eventStartTime: Date
  eventEndTime: Date
  startDayAt: Date
  endDayAt: Date
  minDisplayDurationMinutes: number
}): string => {
  const fiveMinuteIncrement = 5
  const headingRowSpacer = 1
  // If the event starts before the rendered day, clip it
  const trimmedStart = max([args.eventStartTime, args.startDayAt])
  const eventDurationInMinutes = differenceInMinutes(
    args.eventEndTime,
    trimmedStart,
  )
  const minutesAheadOfDayStart = differenceInMinutes(
    trimmedStart,
    args.startDayAt,
  )
  // How many 5-min increments the event's start is past pageStart
  const numerator = Math.round(minutesAheadOfDayStart / fiveMinuteIncrement)
  // How many 5-min increments the event lasts
  const denominator = isAfter(args.eventEndTime, args.endDayAt)
    ? // event runs past end of day — visually trim to end of day
      Math.ceil(
        differenceInMinutes(
          args.endDayAt,
          addMinutes(args.startDayAt, minutesAheadOfDayStart),
        ) / fiveMinuteIncrement,
      )
    : // otherwise use the event's intrinsic length, clamped to a minimum
      Math.ceil(
        Math.max(eventDurationInMinutes, args.minDisplayDurationMinutes) /
          fiveMinuteIncrement,
      )
  return `${numerator + headingRowSpacer} / span ${denominator}`
}

/** Overlap detection — used to cluster events that share screen horizontal space */

/**
 * 1-min buffer: end→start events (10-11am + 11am-12pm) don't count as
 * overlapping.
 */
const OVERLAP_BUFFER_MS = 60_000

const doesEventOverlap =
  (event: ScheduleEvent) =>
  (overlapping: ScheduleEvent): boolean => {
    const eventStartMs = event.startTime.getTime()
    const eventEndMs = event.endTime.getTime()
    const overlappingStartMs = overlapping.startTime.getTime()
    const overlappingEndMs = overlapping.endTime.getTime()
    return (
      eventStartMs <= overlappingEndMs - OVERLAP_BUFFER_MS &&
      eventEndMs > overlappingStartMs + OVERLAP_BUFFER_MS
    )
  }

/**
 * Connected-components on the interval-overlap graph: returns the events
 * partitioned into groups such that within each group every event is
 * transitively reachable from the others by overlap.
 *
 * Implementation: sort by startTime ascending, then a single linear
 * sweep — O(N log N) total. The prior implementation walked the array
 * for each event with deep-equality lookups and then ran a recursive
 * merge pass over the resulting groups, which on a dense day with ~35
 * events ballooned into ~250ms of positionEvents time.
 *
 * Correctness sketch: with starts sorted ascending, the running group's
 * furthest-out end-time fully characterizes whether a new event chains
 * into it. If `e.startMs` is past `group.maxEndMs - buffer` then no event
 * already in the group (nor any future one — they all start later) can
 * overlap `e`, so the group closes and a new one opens. Otherwise `e`
 * overlaps the event holding `maxEndMs` (and thus joins the group's
 * connected component). Schedule events have durations well past the
 * 1-minute buffer, so the symmetric pairwise predicate reduces to this
 * one-sided check.
 */
const groupEventsByOverlap = (events: ScheduleEvent[]): ScheduleEvent[][] => {
  if (events.length === 0) return []
  const sorted = events
    .slice()
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
  const groups: ScheduleEvent[][] = [[sorted[0]]]
  let groupMaxEndMs = sorted[0].endTime.getTime()
  // eslint-disable-next-line no-restricted-syntax -- linear loop kept; see #1071
  for (let i = 1; i < sorted.length; i++) {
    const e = sorted[i]
    const eStartMs = e.startTime.getTime()
    const eEndMs = e.endTime.getTime()
    if (eStartMs <= groupMaxEndMs - OVERLAP_BUFFER_MS) {
      groups[groups.length - 1].push(e)
      if (eEndMs > groupMaxEndMs) groupMaxEndMs = eEndMs
    } else {
      groups.push([e])
      groupMaxEndMs = eEndMs
    }
  }
  return groups
}

const eventsStartWithinNumMinutes =
  (numMinutes: number) => (a: ScheduleEvent) => (b: ScheduleEvent) =>
    Math.abs(differenceInMinutes(a.startTime, b.startTime)) < numMinutes

/**
 * Is `event` before `eventToCompare` (by startTime)?
 */
const eventStartsBeforeEvent =
  (eventToCompare: ScheduleEvent) => (event: ScheduleEvent) =>
    event.startTime.getTime() < eventToCompare.startTime.getTime()

// ── Columnation — the `columnateBy` prop partitions events into columns ──

export const getColumnEventIsIn =
  <T extends ScheduleEvent>(columnateBy: DayScheduleProps<T>['columnateBy']) =>
  (event: Partial<T>) => {
    if (typeof columnateBy === 'function') return columnateBy(event)
    if (typeof columnateBy === 'string')
      return pipe(
        event,
        R.path(columnateBy.split('.')),
        R.ifElse(
          R.isNotNil,
          (value) => ({
            key: String(value),
            headingComponent: String(value),
            // the path, not the value, is what a create form needs
            defaultCreateData: R.assocPath(columnateBy.split('.'), value, {}),
          }),
          R.always(undefined),
        ),
      )
    return undefined
  }

const getColumnKey =
  <T extends ScheduleEvent>(columnateBy: DayScheduleProps<T>['columnateBy']) =>
  (event: Partial<T>) =>
    getColumnEventIsIn(columnateBy)(event)?.key

const columnateEvents = <T extends ScheduleEvent>(
  events: T[],
  ghostEvents: DayScheduleProps<T>['columnateDefaultData'],
  columnateBy?: DayScheduleProps<T>['columnateBy'],
  sortColumns?: DayScheduleProps<T>['sortColumns'],
) => {
  const unColumnatedEvents = !columnateBy
    ? events
    : events.filter(flow(getColumnKey(columnateBy), R.isNil))

  const columnatedEvents = !columnateBy
    ? []
    : pipe(
        [...events, ...(ghostEvents || [])],
        (events) => (sortColumns ? events.sort(sortColumns) : events),
        R.filter<Partial<T>>(flow(getColumnKey(columnateBy), R.isNotNil)),
        R.groupBy((event) => getColumnKey(columnateBy)(event) ?? ''),
        R.values,
        rejectNil,
        (columns) =>
          columns.map((col) =>
            col.filter((event) => !ghostEvents?.some((e) => equals(e, event))),
          ),
      )

  // eslint-disable-next-line no-restricted-syntax -- pre-existing cast; see #1071
  return [unColumnatedEvents, columnatedEvents as T[][]] as const
}

export const getColumnHeadings = <T extends ScheduleEvent>(
  events: T[],
  ghostEvents: DayScheduleProps<T>['columnateDefaultData'],
  columnateBy: DayScheduleProps<T>['columnateBy'],
  sortColumns: DayScheduleProps<T>['sortColumns'],
): ColumnateByObj<T>[] | undefined => {
  if (!columnateBy) return undefined

  return pipe(
    [...events, ...(ghostEvents || [])],
    (events) => (sortColumns ? events.sort(sortColumns) : events),
    R.map(getColumnEventIsIn(columnateBy)),
    rejectUndefined,
    R.map((heading) => (heading.key ? heading : undefined)),
    rejectUndefined,
    R.uniqBy(R.prop('key')),
    (headings) => (R.isEmpty(headings) ? undefined : headings),
  )
}

// ── Condensing / occlusion — flags events that need compact rendering ──

const MS_PER_MINUTE = 60_000

const condenseEventsIfShorterThan =
  (minutes: number) =>
  (events: ScheduleEvent[]): ScheduleEvent[] => {
    const thresholdMs = minutes * MS_PER_MINUTE
    return events.map((event) => {
      const durationMs = event.endTime.getTime() - event.startTime.getTime()
      return durationMs < thresholdMs ? { ...event, condensed: true } : event
    })
  }

/**
 * Set the named boolean prop to true if this event is occluded by another
 * event starting within the spec'd number of minutes (i.e. their visual
 * columns overlap and the later one paints over this one).
 *
 * `index` comes from `events.map`'s callback rather than a per-element
 * `events.findIndex(R.equals(event))` — that lookup was an O(N²) deep
 * equality pass on every positionEvents call.
 */
const writePropertyIfOccluded =
  (property: string) =>
  <T extends ScheduleEvent & { colStart: number; colWidth: number }>(
    withinMinutes: number | ((event: T) => number),
  ) =>
  (events: T[]) =>
    events.map((event, index) => {
      const distance =
        typeof withinMinutes === 'function'
          ? withinMinutes(event)
          : withinMinutes
      /**
       * Pre-curry once per outer iteration so the inner `.some` doesn't
       * rebuild these closures for every candidate.
       */
      const colsOverlap = eventColsOverlap(event)
      const isWithinOcclusionWindow = eventIsWithinOcclusionDistance(
        event,
        distance,
      )
      /**
       * Single short-circuiting pass over the remaining events. Replaces a
       * `pipe(slice → reject → findIndex → gte)` that materialized three
       * intermediate arrays before deciding occlusion.
       */
      const occluded = events
        .slice(index + 1)
        .some(
          (eventToCompare) =>
            !eventsStartInSameMinute(event, eventToCompare) &&
            colsOverlap(eventToCompare) &&
            isWithinOcclusionWindow(eventToCompare),
        )
      return { ...event, [property]: occluded }
    })

const setCondensedTrueIfOccludedWithinMinutes = (minutesPerRow: number) =>
  writePropertyIfOccluded('condensed')((event) =>
    /**
     * For events longer than one row, check for overlap within one row. For
     * events the same length or shorter, sub 1 minute so we don't flag events
     * with simultaneous start/ends.
     */
    Math.min(
      minutesPerRow,
      differenceInMinutes(event.endTime, event.startTime) - 1,
    ),
  )

/**
 * Unused at present — kept intentionally as an alternate clarity-first
 * heuristic for the `occluded` flag (see positionEvents for where the
 * currently-used heuristic is applied).
 */
const _setOccludedTrueIfAnyEventOverlaps = writePropertyIfOccluded('occluded')(
  (event) => differenceInMinutes(event.endTime, event.startTime),
)

/**
 * O(N²) inner loop helpers use .getTime() arithmetic instead of date-fns to
 * avoid allocations.
 */
const eventsStartInSameMinute = (
  a: ScheduleEvent,
  b: ScheduleEvent,
): boolean =>
  Math.floor(a.startTime.getTime() / MS_PER_MINUTE) ===
  Math.floor(b.startTime.getTime() / MS_PER_MINUTE)

const eventIsWithinOcclusionDistance =
  (earlierEvent: ScheduleEvent, occlusionDistanceInMinutes: number) =>
  (laterEvent: ScheduleEvent) => {
    const earlierMs = earlierEvent.startTime.getTime()
    const laterMs = laterEvent.startTime.getTime()
    if (laterMs <= earlierMs) return false
    /**
     * Truncate to whole minutes to match differenceInMinutes semantics
     * (14m30s → 14).
     */
    const fullMinutesBetween = Math.floor(
      (laterMs - earlierMs) / MS_PER_MINUTE,
    )
    return fullMinutesBetween <= occlusionDistanceInMinutes
  }

const eventColsOverlap =
  (eventA: ScheduleEvent & { colStart: number; colWidth: number }) =>
  (eventB: ScheduleEvent & { colStart: number; colWidth: number }) =>
    anyEquals(true, [
      eventA.colStart + eventA.colWidth > eventB.colStart &&
        eventA.colStart < eventB.colStart + eventB.colWidth,
      eventB.colStart + eventB.colWidth > eventA.colStart &&
        eventB.colStart < eventA.colStart + eventA.colWidth,
    ])

// ── Overflow — N+ overlapping events collapse to a single "X more..." tile ──

const truncateEventsIntoGroup =
  <T extends ScheduleEvent>(numEntries: number) =>
  (events: T[]) => {
    if (events.length <= numEntries) return events

    const [shownEvents, groupEvents] = pipe(
      events,
      R.partition<T>((event) => events.indexOf(event) < numEntries - 1),
    )

    return [
      ...shownEvents,
      {
        color: groupEvents[0].color,
        name: `${groupEvents.length} more ${getEventDescriptors(
          groupEvents,
        )}...`,
        startTime: pipe(groupEvents, R.map(R.prop('startTime')), min),
        endTime: pipe(groupEvents, R.map(R.prop('endTime')), max),
        priorityLevel: pipe(
          groupEvents,
          R.map(flow(R.prop('priorityLevel'), R.defaultTo(0))),
          (values) => Math.min(...values),
        ),
        modalContent: groupEvents,
      },
    ]
  }

const getEventDescriptors = <T extends ScheduleEvent>(events: T[]) =>
  pipe(
    events,
    R.map(R.prop('eventDescriptor')),
    R.map(R.defaultTo(['event', 'events'])),
    R.map((d) => (Array.isArray(d) ? d : [d, d + 's'])),
    R.map(R.nth(1)),
    R.uniq,
    R.join('/'),
  )

/**
 * Find how many css columns each event should span so the max number of
 * events can sit side-by-side without any shrinking below `minEventWidth`.
 * Returns the minimum column-count per event.
 */
const getMinColumnsPerEvent = (
  numColumns: number,
  availableSpaceInPixels: number,
  minEventWidth: number,
) => {
  const pixelsPerColumn = availableSpaceInPixels / numColumns
  const columnsPerEvent = pipe(
    (1 / pixelsPerColumn) * minEventWidth,
    Math.floor,
    R.add(1),
  )

  return columnsPerEvent
}

/** Sibling column packing — interval-graph coloring for side-by-side layout */

/**
 * Pack a list of events into the minimum number of columns such that no
 * column contains time-overlapping events. The standard greedy
 * interval-graph coloring algorithm: walk the events in their incoming
 * order (which has already been priority- then start-time-sorted by
 * the caller), placing each event in the leftmost column whose most
 * recent occupant doesn't time-overlap the incoming event.
 *
 * Used to render "sibling" events: events that share a horizontal slot
 * but whose times don't all overlap each other can stack vertically in
 * the same column, so a stagger of e.g. Main Workshop + Side A + Side B
 * + Side C (where A/B/C are sequential) resolves to 2 columns (Main in
 * col 0, A/B/C stacked in col 1) instead of 4 quarter-width slivers.
 */
const packEventsIntoColumns = <T extends ScheduleEvent>(events: T[]): T[][] =>
  events.reduce<T[][]>((columns, event) => {
    const colIdx = columns.findIndex(
      (col) => !doesEventOverlap(event)(col[col.length - 1]),
    )
    if (colIdx === -1) return [...columns, [event]]
    return columns.map((col, i) => (i === colIdx ? [...col, event] : col))
  }, [])

// ── distributeEventsOverColumns — the core recursive layout algorithm ──

const distributeEventsOverColumns =
  (
    numColumns: number,
    columnOffset: number,
    minutesPerRow: number,
    indentationAmount: number,
    minColumnsPerEvent: number,
    totalCols: number,
    /**
     * Depth 0/1: floor at 2 events before overflow tile; depth ≥2: floor
     * drops to 1.
     */
    recursionDepth = 0,
    /**
     * Parent's offset; gap between this and parent becomes
     * indentColStart/indentColWidth.
     */
    parentColumnOffset: number = columnOffset,
    /**
     * Forces all events side-by-side regardless of start time (schedule-wide
     * noOverlap).
     */
    forceAllSiblings = false,
  ) =>
  (
    events: ScheduleEvent[],
  ): (ScheduleEvent & {
    colStart: number
    colWidth: number
    indentColStart?: number
    indentColWidth?: number
  })[] => {
    if (!events.length) return []

    const overlappingEventGroups = groupEventsByOverlap(events)

    // flatMap is correct here; R.flatten was depth-unbounded and wasted work.
    return overlappingEventGroups.flatMap((events) => {
        // Sort priority desc; start time breaks ties within a priority bucket.
        const overlappingEvents = events.sort((a, b) => {
          const priorityDiff = (b.priorityLevel ?? 0) - (a.priorityLevel ?? 0)
          if (priorityDiff !== 0) return priorityDiff
          return a.startTime.getTime() - b.startTime.getTime()
        })

        /**
         * Siblings start near priority event; noOverlap forces any event into
         * the sibling partition.
         */
        const startsWithinSiblingWindow = eventsStartWithinNumMinutes(
          minutesPerRow / 2,
        )(overlappingEvents[0])
        const [siblingEvents, staggeredEvents] = pipe(
          overlappingEvents,
          R.partition<ScheduleEvent>(
            (event) =>
              forceAllSiblings ||
              !!event.noOverlap ||
              startsWithinSiblingWindow(event),
          ),
        )

        /**
         * Max side-by-side cols before overflow tile; floor at 2 for depth
         * 0-1, 1 for deeper.
         */
        const truncateAfterNumCols = pipe(
          numColumns / minColumnsPerEvent,
          Math.floor,
          R.max<number>(recursionDepth <= 1 ? 2 : 1),
        )

        /**
         * Pack non-overlapping siblings into shared cols (2 instead of 4
         * quarter-slivers).
         */
        const packedSiblingColumns = packEventsIntoColumns(siblingEvents)
        const indent =
          columnOffset > parentColumnOffset
            ? {
                indentColStart: parentColumnOffset + 1,
                indentColWidth: columnOffset - parentColumnOffset,
              }
            : {}
        const siblingTiles =
          packedSiblingColumns.length > truncateAfterNumCols
            ? truncateEventsIntoGroup(truncateAfterNumCols)(siblingEvents).map(
                (e, i, all) => {
                  const colWidth = Math.ceil(numColumns / all.length)
                  const colStart = i * colWidth + 1 + columnOffset
                  return { ...e, colStart, colWidth, ...indent }
                },
              )
            : packedSiblingColumns.flatMap((col, colIdx, all) =>
                col.map((e) => {
                  const colWidth = Math.ceil(numColumns / all.length)
                  const colStart = colIdx * colWidth + 1 + columnOffset
                  return { ...e, colStart, colWidth, ...indent }
                }),
              )

        // Below = before priority (paint under); Above = after priority (paint over).
        const [staggeredEventsBelow, staggeredEventsAbove] = pipe(
          staggeredEvents,
          R.partition(eventStartsBeforeEvent(overlappingEvents[0])),
        )

        return [
          ...distributeEventsOverColumns(
            numColumns - 1 * indentationAmount,
            columnOffset + 1 * indentationAmount,
            minutesPerRow,
            indentationAmount,
            minColumnsPerEvent,
            totalCols,
            recursionDepth + 1,
            columnOffset,
            forceAllSiblings,
          )(staggeredEventsBelow),

          ...siblingTiles,

          ...distributeEventsOverColumns(
            numColumns -
              (staggeredEventsBelow.length ? 2 : 1) * indentationAmount,
            columnOffset +
              (staggeredEventsBelow.length ? 2 : 1) * indentationAmount,
            minutesPerRow,
            indentationAmount,
            minColumnsPerEvent,
            totalCols,
            recursionDepth + 1,
            columnOffset,
            forceAllSiblings,
          )(staggeredEventsAbove),
        ]
      })
  }

// ── positionEvents — the single entry point used by HourlySchedule ──

/**
 * Position events within the schedule grid. Assigns each event a
 * `colStart`/`colWidth` plus `condensed`/`occluded` flags for compact
 * rendering. Events without a column assignment (from columnateBy)
 * span full width; events with one are distributed within their column.
 *
 * @param scheduleWidth Pixel width of the outermost schedule container —
 *        used to decide how many events can sit side-by-side before
 *        spilling into an overflow tile.
 */
export const positionEvents = <T extends ScheduleEvent>(
  events: T[],
  // Ghost events seed columns but are stripped before rendering
  ghostEvents: DayScheduleProps<T>['columnateDefaultData'],
  minutesPerRow: number,
  indentationAmount: number,
  columnateByEventProp: DayScheduleProps<T>['columnateBy'],
  sortColumns: DayScheduleProps<T>['sortColumns'],
  scheduleWidth: number,
  minEventWidth: number,
  /**
   * Optional per-column-group weights. When provided, the grid is divided
   * proportionally so a column with many concurrent events gets more
   * pixel width than one with a single non-overlapping event. Compute
   * with `getColumnGroupWeights`. When omitted, column groups share the
   * grid equally.
   */
  columnGroupWeights?: number[],
  /**
   * Schedule-wide override: when true, every overlapping event renders
   * side-by-side instead of staggering. Per-event opt-in lives on the
   * event itself (`event.noOverlap`).
   */
  forceAllSiblings = false,
): T[] => {
  const [unColumnatedEvents, columnatedEvents] = columnateEvents(
    events,
    ghostEvents,
    columnateByEventProp,
    sortColumns,
  )

  const totalCols = getTotalDayScheduleColumns(columnatedEvents.length)

  /**
   * CSS Grid can expand past scheduleWidth; use the larger of measured vs.
   * ideal (maxConcurrent × minEventWidth per venue) so the algorithm and CSS
   * agree on layout.
   */
  const columnatedIdealWidth = (columnGroupWeights ?? []).reduce(
    (sum, w) => sum + w * minEventWidth,
    0,
  )
  const unColumnatedIdealWidth =
    maxConcurrentEvents(unColumnatedEvents) * minEventWidth
  const idealWidth = Math.max(columnatedIdealWidth, unColumnatedIdealWidth)
  const effectiveScheduleWidth = Math.max(scheduleWidth, idealWidth)

  // eslint-disable-next-line no-restricted-syntax -- pre-existing cast; see #1071
  const positionedUnColumnated = pipe(
    unColumnatedEvents,
    distributeEventsOverColumns(
      totalCols,
      0,
      minutesPerRow,
      indentationAmount,
      getMinColumnsPerEvent(totalCols, effectiveScheduleWidth, minEventWidth),
      totalCols,
      0,
      0,
      forceAllSiblings,
    ),
    setCondensedTrueIfOccludedWithinMinutes(minutesPerRow - 1),
    writePropertyIfOccluded('occluded')(minutesPerRow * 0.5),
    condenseEventsIfShorterThan(minutesPerRow),
  ) as PositionedEvent<T>[]

  // eslint-disable-next-line no-restricted-syntax -- pre-existing cast; see #1071
  const positionedColumnated = columnatedEvents
    .map((group, i) => {
      const { start, span } = getColumnGroupAllocation(
        columnatedEvents.length,
        i,
        columnGroupWeights,
      )
      /**
       * distributeEventsOverColumns expects a 0-indexed columnOffset (it adds
       * 1 inside). `start - 1` gives that.
       */
      const columnOffset = start - 1

      return pipe(
        group,
        distributeEventsOverColumns(
          span,
          columnOffset,
          minutesPerRow,
          indentationAmount,
          getMinColumnsPerEvent(
            span,
            effectiveScheduleWidth * (span / totalCols),
            minEventWidth,
          ),
          totalCols,
          0,
          columnOffset,
          forceAllSiblings,
        ),
        setCondensedTrueIfOccludedWithinMinutes(minutesPerRow - 1),
        writePropertyIfOccluded('occluded')(minutesPerRow * 0.5),
        condenseEventsIfShorterThan(minutesPerRow),
      )
    })
    .flat() as PositionedEvent<T>[]

  /**
   * Dodge columnated events that would obscure unColumnated titles; only
   * chase the title position to avoid squeezing multi-cluster overlaps to a
   * thin strip.
   */
  // eslint-disable-next-line no-restricted-syntax -- pre-existing cast; see #1071
  const dodgedUnColumnated = positionedUnColumnated.map((event) =>
    dodgeColumnatedEvents(event, positionedColumnated, totalCols),
  ) as T[]

  // eslint-disable-next-line no-restricted-syntax -- pre-existing cast; see #1071
  return [...dodgedUnColumnated, ...(positionedColumnated as T[])]
}

type PositionedEvent<T extends ScheduleEvent> = T & {
  colStart: number
  colWidth: number
  indentColStart?: number
  indentColWidth?: number
}

const dodgeColumnatedEvents = <T extends ScheduleEvent>(
  event: PositionedEvent<T>,
  columnated: PositionedEvent<T>[],
  totalCols: number,
): PositionedEvent<T> => {
  /**
   * Shift right past each blocker in turn; called repeatedly until no blocker
   * remains.
   */
  const findBlocker = (colStart: number) =>
    columnated.find(
      (c) =>
        doesEventOverlap(event)(c) &&
        c.colStart <= colStart &&
        c.colStart + c.colWidth > colStart,
    )

  /**
   * Walk right past each blocker in turn. Each step clears at least one
   * blocker, so we can never need more steps than `columnated.length`; the
   * bound terminates the recursion if input ever ends up cyclic.
   */
  const shiftPastBlockers = (
    colStart: number,
    stepsRemaining: number,
  ): number => {
    if (stepsRemaining <= 0) return colStart
    const blocker = findBlocker(colStart)
    if (!blocker) return colStart
    const next = blocker.colStart + blocker.colWidth
    if (next <= colStart) return colStart
    return shiftPastBlockers(next, stepsRemaining - 1)
  }
  const shifted = shiftPastBlockers(event.colStart, columnated.length)

  if (shifted === event.colStart) return event

  /**
   * If the shift would squeeze the event into a sliver on the right edge,
   * keep the original position and accept the visual collision instead.
   */
  const minWidth = 4
  if (shifted > totalCols - minWidth) return event

  return {
    ...event,
    colStart: shifted,
    colWidth: totalCols - shifted + 1,
  }
}
